import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { normalizeObjectStrings } from '../lib/utils';
import ImpersonationBanner from '../components/ImpersonationBanner';

const BACKEND_URL =
  window.location.hostname.endsWith('trycloudflare.com')
    ? window.location.origin
    : (process.env.REACT_APP_BACKEND_URL || window.location.origin);

const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(normalizeObjectStrings(response.data));
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password }, {
      withCredentials: true
    });
    if (!response.data?.requires_2fa) {
      setUser(normalizeObjectStrings(response.data));
    }
    return response.data;
  };

  const betaBypassLogin = async () => {
    const response = await axios.post(`${API}/auth/beta-bypass`, {}, {
      withCredentials: true
    });
    setUser(normalizeObjectStrings(response.data));
    return response.data;
  };

  const verifyTwoFactor = async (challengeId, code, rememberDevice = false) => {
    const response = await axios.post(
      `${API}/auth/2fa/verify`,
      {
        challenge_id: challengeId,
        code,
        remember_device: rememberDevice
      },
      { withCredentials: true }
    );
    setUser(normalizeObjectStrings(response.data));
    return response.data;
  };

  const resendTwoFactorCode = async (challengeId) => {
    const response = await axios.post(
      `${API}/auth/2fa/resend`,
      { challenge_id: challengeId },
      { withCredentials: true }
    );
    return response.data;
  };

  const requestPasswordReset = async (email) => {
    const response = await axios.post(
      `${API}/auth/password/reset/request`,
      { email },
      { withCredentials: true }
    );
    return response.data;
  };

  const confirmPasswordReset = async (challengeId, code, newPassword) => {
    const response = await axios.post(
      `${API}/auth/password/reset/confirm`,
      {
        challenge_id: challengeId,
        code,
        new_password: newPassword
      },
      { withCredentials: true }
    );
    return response.data;
  };

  const register = async (email, password, name, partnerCode = '') => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name, partner_code: partnerCode || null }, {
      withCredentials: true
    });
    if (!response.data?.requires_2fa) {
      setUser(normalizeObjectStrings(response.data));
    }
    return response.data;
  };

  const loginWithGoogle = (nextPath = '/dashboard', options = {}) => {
    const params = new URLSearchParams();
    const safeNext =
      typeof nextPath === 'string' && nextPath.startsWith('/') && !nextPath.startsWith('//')
        ? nextPath
        : '/dashboard';
    params.set('next', safeNext);
    params.set('flow', options?.flow === 'register' ? 'register' : 'login');

    const partnerCode = (options?.partnerCode || '').trim().toUpperCase();
    if (partnerCode) {
      params.set('partner_code', partnerCode);
    }

    window.location.href = `${API}/auth/google/login?${params.toString()}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        withCredentials: true
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
  };

  const stopImpersonation = async () => {
    const response = await axios.post(
      `${API}/admin/impersonation/stop`,
      {},
      { withCredentials: true }
    );
    setUser(null);
    return response.data;
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  const value = {
    user,
    loading,
    login,
    betaBypassLogin,
    verifyTwoFactor,
    resendTwoFactorCode,
    requestPasswordReset,
    confirmPasswordReset,
    register,
    loginWithGoogle,
    logout,
    stopImpersonation,
    refreshUser,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      <ImpersonationBanner user={user} onStopImpersonation={stopImpersonation} />
      {children}
    </AuthContext.Provider>
  );
};
